<template>
    <modal ref="modal" id="modal-extract" size="lg">
        <template slot="title">{{ t('balance-detailed_usage_statement') }}</template>
        <template slot="body">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-sm">
                <p class="text-muted mb-0">{{ t('balance-total_records_found').replace('{count}', totalRecords) }}</p>
                <KvButton variant="outlined" prepend-icon="ui-download-01" @click="downloadCsv" :loading="downloadingCsv" class="btn-download-modal">
                    {{ t('balance-download_csv') }}
                </KvButton>
            </div>
            
            <div class="table-responsive">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>{{ t('balance-date_time') }}</th>
                            <th>{{ t('balance-automation_campaign') }}</th>
                            <th>{{ t('balance-action') }}</th>
                            <th>{{ t('balance-credits_consumed') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="4" class="text-center text-muted py-4">{{ t('balance-loading_data') }}</td>
                        </tr>
                        <template v-else>
                            <tr v-for="item in extractData" :key="item.id">
                                <td>
                                    <human-date :date="item.date" format="[d]/[M]/[YY] [h]:[m]" />
                                </td>
                                <td>
                                    {{ item.automation_title }}
                                    <br>
                                    <span v-if="item.automation_type !== 'other'" class="badge badge-light border">
                                        {{ item.automation_type === 'automation' ? t('balance-automation') : t('balance-campaign') }}
                                    </span>
                                </td>
                                <td>{{ formatActionType(item.action_type) }}</td>
                                <td class="text-danger">-{{ formatNumber(item.amount, 2) }}</td>
                            </tr>
                            <tr v-if="!extractData.length">
                                <td colspan="4" class="text-center text-muted">{{ t('balance-no_records_found') }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-sm" v-if="totalPages > 1">
                <div class="text-muted">
                    {{ t('balance-page_x_of_y').replace('{current}', currentPage).replace('{total}', totalPages) }}
                </div>
                <div class="d-flex gap-sm">
                    <KvButton variant="outlined" :disabled="currentPage === 1 || loading" @click="changePage(currentPage - 1)">{{ t('balance-previous') }}</KvButton>
                    <KvButton variant="outlined" :disabled="currentPage === totalPages || loading" @click="changePage(currentPage + 1)">{{ t('balance-next') }}</KvButton>
                </div>
            </div>
        </template>
        <template slot="footer">
            <div class="d-flex justify-content-between align-items-center w-100 flex-wrap gap-sm modal-statement-footer">
                <KvButton variant="ghost" prepend-icon="ui-file-02" @click="goToFullStatement">
                    {{ t('balance-view_full_statement') }}
                </KvButton>
                <KvButton variant="outlined" @click="close">{{ t('balance-close') }}</KvButton>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "../../components/modal/modal";
import KvButton from "../../components/form/kv-button.vue";
import HumanDate from "../../components/human-date/human-date.vue";
import moment from "moment";
import keviApi from "../../keviApi";
import FormatMixin from "../mixins/format-mixin.js";

export default {
    mixins: [FormatMixin],
    components: { Modal, KvButton, HumanDate },
    data() {
        return {
            loading: false,
            downloadingCsv: false,
            currentPage: 1,
            itemsPerPage: 10,
            totalPages: 1,
            totalRecords: 0,
            extractData: [],
            appliedFilters: {}
        };
    },
    methods: {
        open(filters = {}) {
            this.appliedFilters = filters;
            this.currentPage = 1;
            this.fetchData();
            this.$refs.modal.open();
        },
        close() {
            this.$refs.modal.close();
        },
        goToFullStatement() {
            this.close();
            this.$router.push('/credit-statement');
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.fetchData();
            }
        },
        async fetchData() {
            this.loading = true;
            try {
                const filtersStr = encodeURIComponent(JSON.stringify(this.appliedFilters));
                const url = `transactions/credits-extract?page=${this.currentPage}&quantityPerPage=${this.itemsPerPage}&filters=${filtersStr}`;
                const options = keviApi.getOptions(url, "GET");
                const response = await this.$http(options);
                
                if (response && response.status === 200) {
                    this.extractData = response.body.data;
                    this.totalPages = response.body.totalPages;
                    this.totalRecords = response.body.total;
                    this.currentPage = response.body.page;
                }
            } catch (err) {
                console.error("Erro ao buscar histórico de créditos:", err);
                this.$store.dispatch('notification/error', this.t('balance-load_statement_error'));
            } finally {
                this.loading = false;
            }
        },
        async downloadCsv() {
            if (this.downloadingCsv) return;
            this.downloadingCsv = true;
            try {
                const filtersStr = encodeURIComponent(JSON.stringify(this.appliedFilters));
                const infoUrl = `transactions/export-csv-info?filters=${filtersStr}`;
                const infoRes = await this.$http(keviApi.getOptions(infoUrl, 'GET'));
                
                if (infoRes && infoRes.status === 200) {
                    const { chunks } = infoRes.body;
                    for (let i = 0; i < chunks; i++) {
                        const csvUrl = `transactions/export-csv?filters=${filtersStr}&chunk=${i}`;
                        const csvRes = await this.$http(keviApi.getOptions(csvUrl, 'GET'));
                        
                        if (csvRes && csvRes.status === 200) {
                            const blob = new Blob([csvRes.body], { type: 'text/csv;charset=utf-8;' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            const dateLabel = moment().format('YYYY-MM-DD');
                            link.href = url;
                            link.setAttribute('download', `extrato-creditos-${dateLabel}${chunks > 1 ? '-parte-' + (i + 1) : ''}.csv`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        }
                    }
                }
            } catch (err) {
                console.error("Erro ao baixar CSV:", err);
                this.$store.dispatch('notification/error', this.t('balance-prepare_csv_error'));
            } finally {
                this.downloadingCsv = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.gap-sm { gap: 8px; }
.table th { font-weight: 500; font-size: 13px; color: #6B7280; }
.table td { font-size: 13px; vertical-align: middle; }

@media (max-width: 576px) {
    .btn-download-modal {
        width: 100%;
        justify-content: center;
    }

    .modal-statement-footer {
        flex-direction: column-reverse;
        
        button {
            width: 100%;
            justify-content: center;
        }
    }
}
</style>
